import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import ReportDetailLayout from "../../components/ReportDetailLayout";
import ReportFilterBar, {
  type ReportFilterState,
} from "../../components/ReportFilterBar";
import ReportKPICard from "../../components/ReportKPICard";
import ReportChart from "../../components/ReportChart";
import { useReportData } from "../../hooks/useReportData";
import { useReportCategories } from "../../hooks/useReportCategories";
import { useReportConfig } from "../../hooks/useReportConfig";
import {
  reportsExtendedApi,
  type EducationManagerReportResponse,
  type EducationManagerReportItem,
  type EducationManagerReportParams,
} from "../../services/reports-extended.api";
import { EmployeeManagement, Approve } from "@/components/icons";

const PAGE_SIZE = 20;

/**
 * Eğitim Yöneticisi bazlı rapor: her yöneticinin yönettiği eğitim sayısı,
 * katılımcı, tamamlama ve sertifika metrikleri; tarih aralığı filtresiyle.
 */
const EducationManagersReportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  const categories = useReportCategories();

  const params: EducationManagerReportParams = useMemo(
    () => ({
      page,
      limit: PAGE_SIZE,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      search: filter.search?.trim() || undefined,
      categoryIds: filter.categoryIds,
    }),
    [filter, page],
  );

  const { data, isLoading, error } = useReportData<
    EducationManagerReportResponse,
    EducationManagerReportParams
  >({
    fetcher: (p, signal) =>
      reportsExtendedApi.getEducationManagersReport(p, signal),
    params,
  });

  const summary = data?.summary;
  const items = data?.items ?? [];
  const count = data?.count ?? 0;

  const topByParticipants = [...items]
    .filter((i) => i.totalParticipants > 0)
    .sort((a, b) => b.totalParticipants - a.totalParticipants)
    .slice(0, 10);

  const columns: TableColumn<EducationManagerReportItem>[] = [
    {
      key: "fullName",
      label: t("reports.educationManagers.headerManager"),
      header: t("reports.educationManagers.headerManager"),
      accessor: (row) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontWeight: 600 }}>{row.fullName}</span>
          {row.email && (
            <span style={{ fontSize: 12, color: "#64748b" }}>{row.email}</span>
          )}
        </div>
      ),
    },
    {
      key: "departmentName",
      label: t("reports.educationManagers.headerDepartment"),
      header: t("reports.educationManagers.headerDepartment"),
      accessor: (row) => row.departmentName ?? "—",
    },
    {
      key: "educationCount",
      label: t("reports.educationManagers.headerEducations"),
      header: t("reports.educationManagers.headerEducations"),
      accessor: (row) => row.educationCount,
    },
    {
      key: "totalParticipants",
      label: t("reports.educationManagers.headerParticipants"),
      header: t("reports.educationManagers.headerParticipants"),
      accessor: (row) => row.totalParticipants,
    },
    {
      key: "completedCount",
      label: t("reports.educationManagers.headerCompleted"),
      header: t("reports.educationManagers.headerCompleted"),
      accessor: (row) => row.completedCount,
    },
    {
      key: "completionPercent",
      label: t("reports.educationManagers.headerCompletionPct"),
      header: t("reports.educationManagers.headerCompletionPct"),
      accessor: (row) => `%${row.completionPercent.toFixed(1)}`,
    },
    {
      key: "certificateCount",
      label: t("reports.educationManagers.headerCertificates"),
      header: t("reports.educationManagers.headerCertificates"),
      accessor: (row) => row.certificateCount,
    },
  ];

  const cfg = useReportConfig<EducationManagerReportItem>({
    reportKey: "EDUCATION_MANAGERS",
    reportTitle: t("reports.educationManagers.title"),
    columns,
  });

  useEffect(() => {
    if (cfg.defaultFilters) setFilter(cfg.defaultFilters as ReportFilterState);
  }, [cfg.defaultFilters]);

  return (
    <ReportDetailLayout
      title={t("reports.educationManagers.title")}
      layoutKey={`report:EDUCATION_MANAGERS`}
      presetSlot={cfg.presetBar(filter as Record<string, unknown>, (f) =>
        setFilter(f as ReportFilterState),
      )}
      modalSlot={cfg.scheduleModal(filter as Record<string, unknown>)}
      categoryLabel={t("reportCatalog.categories.strategic.title")}
      description={t("reports.educationManagers.description")}
      exportSlot={cfg.toolbar}
      filterSlot={
        <ReportFilterBar
          categories={categories}
          showCategory
          value={filter}
          onChange={(next) => {
            setFilter(next);
            setPage(1);
          }}
          showDepartment={false}
        />
      }
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 14,
        }}
      >
        <ReportKPICard
          title={t("reports.educationManagers.totalManagers")}
          value={summary?.totalManagers ?? 0}
          icon={<EmployeeManagement width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.educationManagers.totalEducations")}
          value={summary?.totalEducations ?? 0}
          icon={<EmployeeManagement width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.educationManagers.totalParticipants")}
          value={summary?.totalParticipants ?? 0}
          icon={<EmployeeManagement width={18} height={18} />}
        />
        <ReportKPICard
          title={t("reports.educationManagers.totalCompleted")}
          value={summary?.totalCompleted ?? 0}
          icon={<Approve width={18} height={18} />}
          accent="success"
        />
      </div>

      <ReportChart
        title={t("reports.educationManagers.topByParticipants")}
        type="bar"
        isLoading={isLoading}
        isEmpty={topByParticipants.length === 0}
        series={[
          {
            name: t("reports.educationManagers.headerParticipants"),
            data: topByParticipants.map((i) => i.totalParticipants),
          },
        ]}
        options={{
          xaxis: { categories: topByParticipants.map((i) => i.fullName) },
          plotOptions: {
            bar: { horizontal: true, borderRadius: 4, barHeight: "70%" },
          },
          colors: ["#4f46e5"],
        }}
      />

      {cfg.columnSettings}

      <Table
        columns={cfg.columns}
        data={items}
        isLoading={isLoading}
        getRowId={(row) => row.managerID}
        totalRecords={count}
        currentPage={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyMessage={
          error
            ? t("reports.educationManagers.noData")
            : t("reports.educationManagers.noManagers")
        }
      />
    </ReportDetailLayout>
  );
};

export default EducationManagersReportPage;
